import { Controller } from 'stimulus';

export default class extends Controller {
  static get values() {
    return { state: String, dropdownOpen: Boolean };
  }

  static get targets() {
    return [
      'countryInput',
      'stateInput',
      'countryButton',
      'stateButton',
      'dropdown',
      'stateDropdown',
      'countryName',
      'stateName',
      'stateOptions',
    ];
  }

  connect() {
    this.fillStatesField();
    this.dropdownOpenValue = false;
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    document.addEventListener('click', this.handleOutsideClick);
  }

  disconnect() {
    document.removeEventListener('click', this.handleOutsideClick);
  }

  setCountry(event) {
    event.preventDefault();

    const country = event.target.dataset.country;
    const countryName = event.target.dataset.countryName;

    this.countryInputTarget.value = country;
    this.countryInputTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.countryNameTarget.textContent = countryName;

    // Clear any validation error messages on country and state fields
    this._clearValidationErrors();

    // Clear state when country changes - always reset regardless of previous value
    this.stateInputTarget.value = '';

    let stateLabel2 = 'State';
    if (this.countryInputTarget.value === 'GB') {
      stateLabel2 = 'County';
    } else if (this.countryInputTarget.value === 'CA') {
      stateLabel2 = 'Province';
    } else {
      stateLabel2 = 'State';
    }
    this.stateNameTarget.textContent = 'Select ' + (stateLabel2 || 'State');
    this._countryChanged = true; // Flag to indicate country was just changed

    // Dispatch custom event for phone input to update its country
    const countryChangeEvent = new CustomEvent('country-changed', {
      detail: { country: country }
    });
    window.dispatchEvent(countryChangeEvent);

    // Reload states for the new country
    this.fillStatesField();
    this.dropdownOpenValue = false;
    this.dropdownTarget.classList.add('hidden');
  }

  setState(event) {
    event.preventDefault();

    const state = event.target.dataset.state;
    const stateName = event.target.dataset.stateName;

    this.stateInputTarget.value = state;
    this.stateInputTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.stateNameTarget.textContent = stateName;
    this.dropdownOpenValue = false;
    this.stateDropdownTarget.classList.add('hidden');
  }

  toggleDropdown(event) {
    event.stopPropagation();
    const button =
      event.target.closest('[data-country-state-target="stateButton"]') ||
      event.target.closest('button');

    if (button) {
      const isCountryDropdown = button.dataset.countryStateTarget === 'countryButton';
      const isStateDropdown = button.dataset.countryStateTarget === 'stateButton';

      if (isCountryDropdown) {
        this.dropdownOpenValue = !this.dropdownOpenValue;
        this.dropdownTarget.classList.toggle('hidden', !this.dropdownOpenValue);
      } else if (isStateDropdown) {
        if (!this.countryInputTarget.value) {
          // Show error message inside the state field wrapper, same as other field errors
          const stateFieldWrapper = this.stateButtonTarget.closest('.rounded-xl') || this.stateButtonTarget.closest('.relative');
          if (stateFieldWrapper) {
            stateFieldWrapper.style.borderColor = '#dc2626';
            stateFieldWrapper.classList.add('is-invalid');
            if (!stateFieldWrapper.querySelector('.country-state-error')) {
              const errorDiv = document.createElement('div');
              errorDiv.className = 'country-state-error px-2 mt-1';
              errorDiv.innerHTML = '<span class="text-xs text-red-600">Please select a Country first</span>';
              stateFieldWrapper.appendChild(errorDiv);
            }
          }
          return;
        }
        this.dropdownOpenValue = !this.dropdownOpenValue;
        this.stateDropdownTarget.classList.toggle('hidden', !this.dropdownOpenValue);
      }
    }
  }

  closeDropdown() {
    this.dropdownOpenValue = false;
    this.dropdownTarget.classList.add('hidden');
    this.stateDropdownTarget.classList.add('hidden');
  }

  handleOutsideClick(event) {
    if (!this.element.contains(event.target)) {
      this.dropdownOpenValue = false;
      this.dropdownTarget.classList.add('hidden');
      this.stateDropdownTarget.classList.add('hidden');
    }
  }

  stopPropagation(event) {
    event.stopPropagation();
  }

  async fillStatesField() {
    // Support both user_state and work_kit_state labels
    const stateLabel = document.querySelector("label[for='user_state']") || document.querySelector("label[for='work_kit_state']");
    const stateButton = this.stateButtonTarget;

    let stateLabel2 = 'State';
    if (stateLabel) {
      let isWorkKit = stateLabel.getAttribute('for') === 'work_kit_state';
      let is_required = stateLabel.dataset.isStateRequired;;
      const suffix = (isWorkKit || is_required=='false') ? '' : '*';

      if (this.countryInputTarget.value === 'GB') {
        stateLabel.innerHTML = 'County' + suffix;
        stateLabel2 = "County";
      } else if (this.countryInputTarget.value === 'CA') {
        stateLabel.innerHTML = 'Province' + suffix;
        stateLabel2 = "Province";
      } else {
        stateLabel.innerHTML = 'State' + suffix;
        stateLabel2 = "State";
      }
    }

    if (!this.countryInputTarget.value) {
      if (this.hasStateOptionsTarget) {
        this.stateOptionsTarget.innerHTML = '';
      }
      return;
    }

    if (this.hasStateOptionsTarget) {
      this.stateOptionsTarget.innerHTML = '';
    }

    try {
      const response = await fetch(`/states?country=${this.countryInputTarget.value}`, {
        credentials: 'same-origin',
      });
      const data = await response.json();

      // Only set state name if we have a valid state value, it exists in the data, and country hasn't just changed
      if (!this._countryChanged && this.stateValue && data[this.stateValue]) {
        this.stateNameTarget.textContent = data[this.stateValue];
        this.stateInputTarget.value = this.stateValue;
      } else {
        // Reset to placeholder if no valid state or country just changed
        this.stateNameTarget.textContent = 'Select ' + (stateLabel2 || 'State');
        this.stateInputTarget.value = '';
        this._countryChanged = false; // Reset the flag
      }

      if (this.hasStateOptionsTarget) {
        Object.keys(data).forEach((key) => {
          const link = document.createElement('a');
          link.href = '#';
          link.classList.add(
            'text-gray-700',
            'block',
            'px-4',
            'py-2',
            'text-sm',
            'hover:bg-gray-100'
          );
          link.dataset.action = 'click->country-state#setState';
          link.dataset.state = key;
          link.dataset.stateName = data[key];
          link.textContent = data[key];
          this.stateOptionsTarget.appendChild(link);
        });
      }
    } catch (error) {
      console.error('Error:', error);
    }
  }

  _clearValidationErrors() {
    // Clear validation errors from input-validation wrappers (personal info page)
    const wrappers = [
      this.countryInputTarget.closest('[data-controller="input-validation"]'),
      this.stateInputTarget.closest('[data-controller="input-validation"]'),
    ];

    wrappers.forEach((wrapper) => {
      if (!wrapper) return;
      wrapper.classList.remove('is-invalid');
      const errorMsg = wrapper.querySelector('.client-validation-error');
      if (errorMsg) errorMsg.remove();
    });

    // Clear country-state-error messages and reset border (works on both pages)
    const stateFieldWrapper = this.stateButtonTarget.closest('.rounded-xl') || this.stateButtonTarget.closest('.relative');
    if (stateFieldWrapper) {
      stateFieldWrapper.classList.remove('is-invalid');
      stateFieldWrapper.style.borderColor = '';
      const stateError = stateFieldWrapper.querySelector('.country-state-error');
      if (stateError) stateError.remove();
    }
  }
}
